import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Printer, Send, MapPin, DoorOpen, Clock, Shirt, Sparkles } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SENDER_ROLES = ["Super Admin", "Campus Coordinator", "Campus Coordinator Supervisor", "Recruiter"];

function Block({ label, value }) {
  if (!value || !value.trim()) return null;
  return (
    <div className="mb-4">
      <div className="text-xs uppercase tracking-wide text-zinc-400 font-semibold mb-1">{label}</div>
      <p className="text-zinc-800 whitespace-pre-line">{value}</p>
    </div>
  );
}

export default function BioView() {
  const { userId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bio, setBio] = useState(null);
  const [err, setErr] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendForm, setSendForm] = useState({ to_email: "", message: "" });
  const [sending, setSending] = useState(false);
  const token = localStorage.getItem("cc_token");
  const canSend = SENDER_ROLES.includes(user?.role);

  useEffect(() => {
    api.get(`/bios/${userId}`).then(({ data }) => setBio(data))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Could not load this bio"));
  }, [userId]);

  const send = async () => {
    if (!sendForm.to_email.trim()) return toast.error("Enter a recipient email");
    setSending(true);
    try { await api.post(`/bios/${userId}/send`, sendForm); toast.success("Bio sent"); setSendOpen(false); setSendForm({ to_email: "", message: "" }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not send"); }
    finally { setSending(false); }
  };

  if (err) return <div className="p-6"><EmptyState text={err} /></div>;
  if (!bio) return <div className="p-6"><EmptyState text="Loading…" /></div>;
  const p = bio.person || {};
  const isCI = bio.kind === "ci";
  const photo = bio.photo_path ? `${BACKEND}/api/bios/${userId}/photo?auth=${token}` : null;

  return (
    <div className="max-w-2xl" data-testid="bio-view-page">
      <div className="flex items-center justify-between mb-4 print:hidden">
        <Button variant="ghost" onClick={() => navigate(-1)} data-testid="bio-back"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.print()} data-testid="bio-print"><Printer className="w-4 h-4 mr-1.5" />Print / PDF</Button>
          {canSend && <Button className="bg-cc-blue text-white" onClick={() => setSendOpen(true)} data-testid="bio-send-open"><Send className="w-4 h-4 mr-1.5" />Email to recruit</Button>}
        </div>
      </div>

      <Card className="p-8">
        {!bio.completed && <div className="text-sm text-amber-600 mb-4 print:hidden" data-testid="bio-incomplete">This bio isn't complete yet.</div>}
        <div className="flex items-center gap-5 mb-6">
          {photo ? <img src={photo} alt={p.name} className="w-24 h-24 rounded-full object-cover border-2 border-cc-blue/20" data-testid="bio-view-photo" />
            : <div className="w-24 h-24 rounded-full bg-cc-blue/10 text-cc-blue flex items-center justify-center text-2xl font-semibold border-2 border-cc-blue/20">{(p.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("")}</div>}
          <div>
            <h1 className="text-2xl font-bold text-zinc-900" data-testid="bio-view-name">{p.name}</h1>
            <p className="text-zinc-500">{isCI ? "Clinical Instructor" : p.role}{p.facility_name ? ` · ${p.facility_name}` : ""}</p>
          </div>
        </div>

        {isCI ? (
          <>
            <Block label="About You" value={bio.about_you} />
            <Block label="About Your Facility" value={bio.about_facility} />
            {(bio.parking_instructions || bio.entrance_instructions || bio.arrival_time) && (
              <div className="mb-4 rounded-lg bg-cc-blue/5 p-4">
                <div className="text-xs uppercase tracking-wide text-cc-blue font-semibold mb-2">Getting there on day one</div>
                <div className="space-y-1.5 text-sm text-zinc-700">
                  {bio.parking_instructions && <p className="flex items-start gap-2"><MapPin className="w-4 h-4 text-cc-blue mt-0.5 shrink-0" />Park: {bio.parking_instructions}</p>}
                  {bio.entrance_instructions && <p className="flex items-start gap-2"><DoorOpen className="w-4 h-4 text-cc-blue mt-0.5 shrink-0" />Entrance to the gym: {bio.entrance_instructions}</p>}
                  {bio.arrival_time && <p className="flex items-start gap-2"><Clock className="w-4 h-4 text-cc-blue mt-0.5 shrink-0" />{bio.arrival_time}</p>}
                </div>
              </div>
            )}
            <Block label="Dress code" value={bio.dress_code} />
          </>
        ) : (
          <>
            {(bio.questions || []).map((q) => <Block key={q.key} label={q.label} value={bio[q.key]} />)}
          </>
        )}
      </Card>

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-cc-blue" />Introduce {p.name}</DialogTitle>
            <DialogDescription>Send this bio by email to introduce {p.name} to a recruit or contact.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wide text-zinc-400 font-semibold">Recruit / recipient email</label>
              <Input value={sendForm.to_email} onChange={(e) => setSendForm({ ...sendForm, to_email: e.target.value })} placeholder="student@school.edu" data-testid="bio-send-email" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-zinc-400 font-semibold">Personal note (optional)</label>
              <Textarea rows={3} value={sendForm.message} onChange={(e) => setSendForm({ ...sendForm, message: e.target.value })} data-testid="bio-send-message" placeholder="We'd love to introduce you to…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)}>Cancel</Button>
            <Button className="bg-cc-blue text-white" onClick={send} disabled={sending} data-testid="bio-send-submit">{sending ? "Sending…" : "Send bio"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
